import ApplicationConfig, { CONNECT_ENV_DEV, CONNECT_ENV_DEVFIRE } from '@/lib/application-config'
import connectHttpBase from '@/lib/connect-client/connect-http-base'
import connectPayClient from '@/lib/connect-client/connect-pay-client'
import * as Constants from '@/lib/constants'
import axios from 'axios'

/**
 * feed-api (施策F ニュースフィード/投稿) のクライアント。
 * 認証: app-pay 発行の feed トークン。フォロー解決のため manager トークンを併送する。
 */
class FeedApiClient {
  async buildHeaders() {
    const tokenRes = await connectPayClient.getFeedAccessToken()
    const token = tokenRes.data && tokenRes.data.accessToken
    const headers = { Authorization: 'Bearer ' + token }
    const managerToken = connectHttpBase.getAccessToken()
    if (managerToken) {
      headers['X-Manager-Authorization'] = 'Bearer ' + managerToken
      headers['X-Manager-Env'] =
        ApplicationConfig.CURRENT_ENV === CONNECT_ENV_DEV || ApplicationConfig.CURRENT_ENV === CONNECT_ENV_DEVFIRE
          ? 'dev'
          : 'prod'
    }
    return headers
  }

  async timeline(params) {
    return axios.get(Constants.FEED_API_URL + '/v1/feed/timeline', { params, headers: await this.buildHeaders() })
  }

  async history(params) {
    return axios.get(Constants.FEED_API_URL + '/v1/feed/history', { params, headers: await this.buildHeaders() })
  }

  async createPost(body) {
    return axios.post(Constants.FEED_API_URL + '/v1/feed/posts', body, { headers: await this.buildHeaders() })
  }

  async toggleKudos(postId) {
    return axios.post(Constants.FEED_API_URL + '/v1/feed/posts/' + postId + '/kudos', null, {
      headers: await this.buildHeaders(),
    })
  }

  async updatePost(postId, body) {
    return axios.patch(Constants.FEED_API_URL + '/v1/feed/posts/' + postId, body, {
      headers: await this.buildHeaders(),
    })
  }

  async deletePost(postId) {
    return axios.delete(Constants.FEED_API_URL + '/v1/feed/posts/' + postId, { headers: await this.buildHeaders() })
  }

  async reportPost(postId, reason) {
    return axios.post(
      Constants.FEED_API_URL + '/v1/feed/posts/' + postId + '/report',
      { reason: reason || '' },
      { headers: await this.buildHeaders() }
    )
  }

  async uploadPhoto(file) {
    const form = new FormData()
    form.append('file', file)
    return axios.post(Constants.FEED_API_URL + '/v1/feed/photos', form, { headers: await this.buildHeaders() })
  }

  // 操作イベントの自動投稿と設定 (R20)
  async getFeedSettings() {
    return axios.get(Constants.FEED_API_URL + '/v1/feed/settings', { headers: await this.buildHeaders() })
  }

  async putFeedSettings(body) {
    return axios.put(Constants.FEED_API_URL + '/v1/feed/settings', body, { headers: await this.buildHeaders() })
  }

  async postFeedEvent(body) {
    return axios.post(Constants.FEED_API_URL + '/v1/feed/events', body, { headers: await this.buildHeaders() })
  }

  async follows() {
    return axios.get(Constants.FEED_API_URL + '/v1/feed/follows', { headers: await this.buildHeaders() })
  }

  async requestFollow(username, nickname) {
    return axios.post(
      Constants.FEED_API_URL + '/v1/feed/follows/' + username,
      { nickname: nickname || '' },
      { headers: await this.buildHeaders() }
    )
  }

  async acceptFollow(username, nickname) {
    return axios.post(
      Constants.FEED_API_URL + '/v1/feed/follows/' + username + '/accept',
      { nickname: nickname || '' },
      { headers: await this.buildHeaders() }
    )
  }

  async declineFollow(username) {
    return axios.post(Constants.FEED_API_URL + '/v1/feed/follows/' + username + '/decline', null, {
      headers: await this.buildHeaders(),
    })
  }

  async unfollow(username) {
    return axios.delete(Constants.FEED_API_URL + '/v1/feed/follows/' + username, {
      headers: await this.buildHeaders(),
    })
  }

  async getRoute(sessionId, taskGroupId, full) {
    return axios.get(Constants.FEED_API_URL + '/v1/routes', {
      params: { session_id: sessionId, task_group_id: taskGroupId, full: full ? 1 : 0 },
      headers: await this.buildHeaders(),
    })
  }

  async workRecords() {
    return axios.get(Constants.FEED_API_URL + '/v1/work-records', { headers: await this.buildHeaders() })
  }

  async createWorkRecord(body) {
    return axios.post(Constants.FEED_API_URL + '/v1/work-records', body, { headers: await this.buildHeaders() })
  }

  async deleteWorkRecord(id) {
    return axios.delete(Constants.FEED_API_URL + '/v1/work-records/' + id, { headers: await this.buildHeaders() })
  }

  async zoneMapDates(body) {
    return axios.post(Constants.FEED_API_URL + '/v1/zone-maps/dates', body, {
      headers: await this.buildHeaders(),
      timeout: 60000,
    })
  }

  async createZoneMap(body) {
    return axios.post(Constants.FEED_API_URL + '/v1/zone-maps', body, {
      headers: await this.buildHeaders(),
      timeout: 120000,
    })
  }

  async zoneMaps() {
    return axios.get(Constants.FEED_API_URL + '/v1/zone-maps', { headers: await this.buildHeaders() })
  }

  async zoneMapDetail(id) {
    return axios.get(Constants.FEED_API_URL + '/v1/zone-maps/' + id, { headers: await this.buildHeaders() })
  }

  async reclassifyZoneMap(id, body) {
    return axios.patch(Constants.FEED_API_URL + '/v1/zone-maps/' + id + '/reclassify', body, {
      headers: await this.buildHeaders(),
      timeout: 120000,
    })
  }

  async updateZoneMap(id, body) {
    return axios.patch(Constants.FEED_API_URL + '/v1/zone-maps/' + id, body, { headers: await this.buildHeaders() })
  }

  async exportZoneMapIsoxml(id, params) {
    return axios.get(Constants.FEED_API_URL + '/v1/zone-maps/' + id + '/isoxml', {
      params,
      headers: await this.buildHeaders(),
      responseType: 'blob',
      timeout: 120000,
    })
  }

  async deleteZoneMap(id) {
    return axios.delete(Constants.FEED_API_URL + '/v1/zone-maps/' + id, { headers: await this.buildHeaders() })
  }

  // 高低差 (GNSS 由来): datastore の高低差メッシュを ELEV ゾーンマップとして取り込む
  async importElevationZoneMap(body) {
    return axios.post(Constants.FEED_API_URL + '/v1/zone-maps/import-elevation', body, {
      headers: await this.buildHeaders(),
      timeout: 120000,
    })
  }

  async importElevationGroup(body) {
    return axios.post(Constants.FEED_API_URL + '/v1/zone-map-groups/import-elevation', body, {
      headers: await this.buildHeaders(),
      timeout: 300000,
    })
  }

  // VRA マップ (グループ): 圃場セットからの生成バッチを1つの選択単位として扱う
  async zoneMapGroups() {
    return axios.get(Constants.FEED_API_URL + '/v1/zone-map-groups', { headers: await this.buildHeaders() })
  }

  async updateZoneMapGroup(id, body) {
    return axios.patch(Constants.FEED_API_URL + '/v1/zone-map-groups/' + id, body, {
      headers: await this.buildHeaders(),
    })
  }

  async deleteZoneMapGroup(id) {
    return axios.delete(Constants.FEED_API_URL + '/v1/zone-map-groups/' + id, {
      headers: await this.buildHeaders(),
    })
  }

  async fieldAttrs() {
    return axios.get(Constants.FEED_API_URL + '/v1/field-attrs', { headers: await this.buildHeaders() })
  }

  async putFieldAttrs(fieldId, body) {
    return axios.put(Constants.FEED_API_URL + '/v1/field-attrs/' + fieldId, body, {
      headers: await this.buildHeaders(),
    })
  }

  // 圃場セット (圃場の上位概念。VRA バッチ生成などの用途側から参照される)
  async fieldSets() {
    return axios.get(Constants.FEED_API_URL + '/v1/field-sets', { headers: await this.buildHeaders() })
  }

  async createFieldSet(body) {
    return axios.post(Constants.FEED_API_URL + '/v1/field-sets', body, { headers: await this.buildHeaders() })
  }

  async updateFieldSet(id, body) {
    return axios.patch(Constants.FEED_API_URL + '/v1/field-sets/' + id, body, { headers: await this.buildHeaders() })
  }

  async deleteFieldSet(id) {
    return axios.delete(Constants.FEED_API_URL + '/v1/field-sets/' + id, { headers: await this.buildHeaders() })
  }

  async fieldSetMaps(id) {
    return axios.get(Constants.FEED_API_URL + '/v1/field-sets/' + id + '/maps', { headers: await this.buildHeaders() })
  }

  async fieldSetFields(id) {
    return axios.get(Constants.FEED_API_URL + '/v1/field-sets/' + id + '/fields', {
      headers: await this.buildHeaders(),
    })
  }

  async replaceFieldSetFields(id, fields) {
    return axios.put(
      Constants.FEED_API_URL + '/v1/field-sets/' + id + '/fields',
      { fields },
      { headers: await this.buildHeaders() }
    )
  }

  async startFieldSetGeneration(id, params) {
    return axios.post(Constants.FEED_API_URL + '/v1/field-sets/' + id + '/generate', params, {
      headers: await this.buildHeaders(),
    })
  }

  async runFieldSetTile(id, jobId, tileIndex) {
    return axios.post(
      Constants.FEED_API_URL + '/v1/field-sets/' + id + '/generate/' + jobId + '/tiles/' + tileIndex,
      null,
      { headers: await this.buildHeaders(), timeout: 300000 }
    )
  }

  async manualRecords() {
    return axios.get(Constants.FEED_API_URL + '/v1/manual-records', { headers: await this.buildHeaders() })
  }

  async createManualRecord(body) {
    return axios.post(Constants.FEED_API_URL + '/v1/manual-records', body, { headers: await this.buildHeaders() })
  }

  async updateManualRecord(id, body) {
    return axios.patch(Constants.FEED_API_URL + '/v1/manual-records/' + id, body, {
      headers: await this.buildHeaders(),
    })
  }

  async deleteManualRecord(id) {
    return axios.delete(Constants.FEED_API_URL + '/v1/manual-records/' + id, { headers: await this.buildHeaders() })
  }
}

export default new FeedApiClient()
